import React, { useContext, useState } from 'react';
import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import {
  Button,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { Link } from 'react-router-dom';
import DevicesIcon from '@mui/icons-material/Devices';
import Checkbox from '@mui/material/Checkbox';
import PersonAddDisabledIcon from '@mui/icons-material/PersonAddDisabled';
import WifiPasswordIcon from '@mui/icons-material/WifiPassword';
import { VARIANTS } from 'shared/constants';
import { formatTenantName } from 'shared/utils/formatters';
import { getTenantMacPath, getTenantPath } from 'shared/routes/tenant';
import { useAuth, useNotification, useRequest } from 'shared/hooks';
import { SideNavContext } from 'shared/hoc/withSideNav';
import { moveTenant } from './moveTenant';
import InternetServiceChanger from './InternetServiceChanger';

/**
 * @param {Object} props
 * @param {Function} props.tenantMoveDispatch - Dispatch function for tenant move state
 * @param {Object} props.tenantMoveState - Tenant move state object
 */
export default function TenantMoveVerification(props) {
  const {
    tenantMoveDispatch,
    tenantMoveState,
  } = props;

  const {
    currentBuilding,
    deviceActions,
    devices,
    forceSubscriptionCancel,
    forceSubscriptionChange,
    movingSamePlan,
    groupDpskWarning,
    hasDevices,
    hasSubscription,
    internetDisallowReason,
    isOrphan,
    isSameUnit,
    newBuilding,
    newBuildingUpgrades,
    onboardAccount,
    subscription,
    tenant,
  } = tenantMoveState;

  const {
    deleteList,
    wiredList,
  } = deviceActions;

  const syncSideNav = useContext(SideNavContext);
  const {
    pushSuccess,
    pushInfo,
    pushError,
  } = useNotification();
  const {
    request,
    isLoading,
  } = useRequest();
  const { user } = useAuth();
  const [state, setState] = useState({
    deviceConfirm: false,
    newPlan: null,
    onboardAccountRemovalConfirm: false,
    subscriptionConfirm: false,
  });

  const {
    deviceConfirm,
    newPlan,
    onboardAccountRemovalConfirm,
    subscriptionConfirm,
  } = state;

  let warning;
  if (internetDisallowReason && (hasSubscription || devices.length > 0)) {
    warning = `Warning: ${internetDisallowReason}. This will cause device removal and subscription cancellation.`;
  }

  const internetAction = forceSubscriptionCancel || forceSubscriptionChange || movingSamePlan
    || (!hasSubscription && !isOrphan && currentBuilding.qos.speed !== newBuilding.qos.speed);
  const confirmed = (subscriptionConfirm || !internetAction) && (deviceConfirm || !hasDevices) && (onboardAccountRemovalConfirm || (newBuilding.auth_method.includes('802.1x') || tenant.onboard_account === null));

  const cancelSub = forceSubscriptionCancel || (forceSubscriptionChange && !newPlan);

  const submit = async () => {
    if (isSameUnit && !hasDevices && !hasSubscription && (tenant.onboard_account === null || newBuilding.auth_method.includes('802.1x'))) {
      pushInfo('Nothing to do');
      return;
    }

    const planId = newPlan && newPlan.id;
    const {
      moved,
      errors,
    } = await moveTenant(request, tenantMoveState, user, cancelSub, planId);

    errors.forEach((error) => pushError(`Move tenant error: ${error}`));
    if (moved) {
      if (errors.length === 0) {
        pushSuccess('Moved tenant');
      } else {
        pushInfo('Moved tenant with errors');
      }
      syncSideNav(getTenantPath(tenant.id), formatTenantName(tenant));
      tenantMoveDispatch({ action: 'move' });
    } else {
      pushError('Failed to move tenant');
    }
  };

  const onPlanChange = (value) => {
    setState({
      ...state,
      newPlan: value,
    });
  };

  return (
    <Grid
      container
      rowSpacing={2}
      sx={{
        justifyContent: 'start',
        alignItems: 'start',
      }}
    >
      {warning && (
        <Grid size={12}>
          <Alert severity={VARIANTS.warning}>
            {warning}
          </Alert>
        </Grid>
      )}
      {!newBuilding.auth_method.includes('802.1x') && onboardAccount !== null && (
        <Grid size={12}>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: 'center',
            }}
          >
            <PersonAddDisabledIcon color="primary" />
            <Typography variant="h6">Onboard Account</Typography>
          </Stack>
          <Typography sx={{ my: 1 }} variant="subtitle1">
            New Building does not support the use of
            an Onboard account.
          </Typography>
          <FormControlLabel
            control={(
              <Checkbox
                checked={onboardAccountRemovalConfirm}
                onChange={() => setState({
                  ...state,
                  onboardAccountRemovalConfirm: !onboardAccountRemovalConfirm,
                })}
              />
            )}
            label="Confirm Onboard account removal"
          />
          <hr />
        </Grid>
      )}
      {hasDevices && (
        <Grid size={12}>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: 'center',
            }}
          >
            <DevicesIcon color="primary" />
            <Typography variant="h6">Device Changes</Typography>
          </Stack>
          {wiredList.length > 0 && (
            <List sx={{ width: '100%' }} aria-label="Device Updates">
              <ListItem disablePadding>
                <ListItemText primary="Update radius records for:" />
              </ListItem>
              {wiredList.map((d) => (
                <ListItem disablePadding key={d.id}>
                  <ListItemText primary={(
                    <Link
                      target="_blank"
                      to={getTenantMacPath(tenant.id, d.id)}
                    >
                      {`${d.name} (${d.mac})`}
                    </Link>
                  )}
                  />
                </ListItem>
              ))}
            </List>
          )}
          {deleteList.length > 0 && (
            <List sx={{ width: '100%' }} aria-label="Device Deletions">
              <ListItem disablePadding>
                <ListItemText primary="Devices being removed:" />
              </ListItem>
              {deleteList.map((d) => (
                <ListItem disablePadding key={d.id}>
                  <ListItemText primary={(
                    <Link
                      target="_blank"
                      to={getTenantMacPath(tenant.id, d.id)}
                    >
                      {`${d.name} (${d.mac})`}
                    </Link>
                  )}
                  />
                </ListItem>
              ))}
            </List>
          )}
          {groupDpskWarning && (
            <Grid size={12}>
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  alignItems: 'center',
                }}
              >
                <WifiPasswordIcon color="primary" />
                <Typography variant="h6">Group DPSK</Typography>
              </Stack>
              <Typography sx={{ my: 1 }} variant="subtitle1">
                The selected unit has group wireless
                keys.
                {` ${formatTenantName(tenant)} will need to `}
                <strong>log into Onboard and view their new wireless password</strong>
                .
              </Typography>
            </Grid>
          )}
          {(!groupDpskWarning && !internetDisallowReason) && (
            <Typography sx={{ my: 1 }} variant="subtitle1">
              All devices should authenticate without
              tenant action.
            </Typography>
          )}
          <FormControlLabel
            control={(
              <Checkbox
                checked={deviceConfirm}
                onChange={() => setState({
                  ...state,
                  deviceConfirm: !deviceConfirm,
                })}
              />
            )}
            label="Confirm device changes"
          />
          <hr />
        </Grid>
      )}
      {internetAction && (
        <InternetServiceChanger
          cancelSub={cancelSub}
          currentBuilding={currentBuilding}
          forceSubscriptionChange={forceSubscriptionChange}
          movingSamePlan={movingSamePlan}
          newBuilding={newBuilding}
          newBuildingUpgrades={newBuildingUpgrades}
          newPlan={newPlan}
          onPlanChange={onPlanChange}
          subscription={subscription}
          subscriptionConfirm={subscriptionConfirm}
          tenant={tenant}
          toggleConfirm={() => setState({
            ...state,
            subscriptionConfirm: !subscriptionConfirm,
          })}
        />
      )}
      <Grid
        size={12}
        sx={{
          display: 'flex',
          justifyContent: 'end',
        }}
      >
        <Button
          disabled={!confirmed || isLoading}
          variant={VARIANTS.contained}
          onClick={submit}
        >
          Submit
        </Button>
      </Grid>
    </Grid>
  );
}
